import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Users as UsersIcon, Shield, Upload, KeyRound, UserX, UserCheck, Pencil, Search, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["admin", "admin_addl", "cad", "production", "merchandiser", "management", "line_supervisor", "ie_assistant", "floor_supervisor", "purchase", "stores", "stores_manager", "stores_jr_asst", "docs_executive", "packing_supervisor", "guard"];
const ROLE_LABELS = {
  admin: "Administrator",
  admin_addl: "Admin Addl",
  cad: "CAD Team",
  production: "Production",
  merchandiser: "Merchandiser",
  management: "Management",
  line_supervisor: "Line Supervisor",
  ie_assistant: "IE Assistant",
  floor_supervisor: "Floor Supervisor",
  purchase: "Purchase",
  stores: "Stores",
  stores_manager: "Stores Manager",
  stores_jr_asst: "Stores Jr. Asst.",
  packing_supervisor: "Packing Supervisor",
  docs_executive: "Docs Executive",
  guard: "Guard",
};
const ROLE_BADGE = {
  admin: "bg-[#7C1D3A]/10 text-[#7C1D3A] border-[#7C1D3A]/20",
  admin_addl: "bg-[#7C1D3A]/5 text-[#7C1D3A] border-[#7C1D3A]/20",
  cad: "bg-blue-50 text-blue-700 border-blue-200",
  production: "bg-amber-50 text-amber-800 border-amber-200",
  merchandiser: "bg-purple-50 text-purple-700 border-purple-200",
  management: "bg-emerald-50 text-emerald-700 border-emerald-200",
  line_supervisor: "bg-teal-50 text-teal-700 border-teal-200",
  ie_assistant: "bg-indigo-50 text-indigo-700 border-indigo-200",
  floor_supervisor: "bg-cyan-50 text-cyan-700 border-cyan-200",
  purchase: "bg-orange-50 text-orange-700 border-orange-200",
  stores: "bg-lime-50 text-lime-800 border-lime-200",
  stores_manager: "bg-lime-100 text-lime-900 border-lime-300",
  stores_jr_asst: "bg-yellow-50 text-yellow-800 border-yellow-200",
  packing_supervisor: "bg-yellow-50 text-yellow-800 border-yellow-200",
  docs_executive: "bg-slate-100 text-slate-700 border-slate-300",
  guard: "bg-rose-50 text-rose-700 border-rose-200",
};

function Avatar({ name }) {
  const letter = (name || "?")[0]?.toUpperCase() || "?";
  return (
    <div className="h-9 w-9 rounded-full bg-[#7C1D3A]/10 text-[#7C1D3A] flex items-center justify-center font-semibold text-sm shrink-0">
      {letter}
    </div>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null); // full user object being edited (null = closed)
  const [resetTarget, setResetTarget] = useState(null); // user object for pwd reset dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [form, setForm] = useState({ email: "", name: "", role: "cad", password: "", phone: "", department: "" });
  const [editForm, setEditForm] = useState({ name: "", role: "cad", phone: "", department: "" });
  const [newPwd, setNewPwd] = useState("");

  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = users;
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((u) => [u.name, u.email, u.department, u.phone].some((x) => (x || "").toLowerCase().includes(s)));
    }
    if (roleFilter !== "all") list = list.filter((u) => u.role === roleFilter);
    if (statusFilter !== "all") list = list.filter((u) => (statusFilter === "active" ? u.is_active !== false : u.is_active === false));
    return list;
  }, [users, q, roleFilter, statusFilter]);

  const add = async () => {
    if (!form.email || !form.password || form.password.length < 6) { toast.error("Email and password (6+ chars) required"); return; }
    setBusy(true);
    try {
      await api.post("/users", form); toast.success("User created");
      setAddOpen(false); setForm({ email: "", name: "", role: "cad", password: "", phone: "", department: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    setBusy(false);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name || "", role: u.role, phone: u.phone || "", department: u.department || "" });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${editUser.id}`, editForm);
      toast.success("User updated");
      setEditUser(null); load();
    } catch (e) {
      // Backend returns array-shaped `detail` on Pydantic validation errors — flatten to a string
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join("; ") : (d || "Update failed");
      toast.error(msg);
    }
    setBusy(false);
  };

  const del = async (u) => {
    if (!confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const toggleActive = async (u) => {
    const action = u.is_active === false ? "activate" : "deactivate";
    if (!confirm(`${action === "activate" ? "Reactivate" : "Deactivate"} ${u.email}?`)) return;
    try { await api.post(`/users/${u.id}/${action}`); toast.success(action === "activate" ? "User reactivated" : "User deactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doReset = async () => {
    if (newPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: newPwd });
      toast.success(`Password reset for ${resetTarget.email}`, { description: "Share the new password securely with the user." });
      setResetTarget(null); setNewPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    setBusy(false);
  };

  const roleCount = new Set(users.map((u) => u.role)).size;

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase inline-flex items-center gap-1.5"><UsersIcon className="h-3.5 w-3.5" /> Administration</div>
          <h1 className="mt-1 font-heading font-bold text-3xl tracking-tight">User Management</h1>
          <p className="text-slate-500 text-[13px] mt-1" data-testid="users-meta">{users.length} users · {roleCount} roles</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => nav("/admin/roles-permissions")} data-testid="roles-permissions-btn"><Shield className="h-4 w-4 mr-2" /> Roles &amp; Permissions</Button>
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-upload-btn"><Upload className="h-4 w-4 mr-2" /> Bulk Upload</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-gatekeepers-btn"><ShieldCheck className="h-4 w-4 mr-2" /> Import from Asset IQ</Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand hover:bg-brand-dark text-white" data-testid="users-add-btn"><Plus className="h-4 w-4 mr-2" /> Add User</Button>
            </DialogTrigger>
            <DialogContent data-testid="add-user-dialog">
              <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
                  <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input placeholder="Department (e.g. CAD, Merch, IT)" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="new-user-department" />
                </div>
                <Input placeholder="Phone (optional, e.g. +91…)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="new-user-phone" />
                <Input type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button onClick={add} disabled={busy} className="bg-brand hover:bg-brand-dark text-white" data-testid="new-user-confirm">{busy ? "Creating…" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 border-slate-200 rounded-md bg-white">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, department, phone…" data-testid="users-search" className="w-full h-10 pl-9 pr-3 rounded-md bg-slate-50 border border-slate-200 text-sm outline-none focus:bg-white focus:border-slate-300" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="users-filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="users-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Deactivated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 rounded-md bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="opsiq-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Department</th>
                <th>Phone</th>
                <th className="text-right" style={{ width: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className={u.is_active === false ? "opacity-60" : ""}>
                  <td>
                    <div className="flex items-center gap-3">
                      <Avatar name={u.name} />
                      <div>
                        <div className="font-semibold">{u.name}</div>
                        {u.is_active === false && <div className="text-[10.5px] font-mono uppercase tracking-widest text-rose-600">Deactivated</div>}
                      </div>
                    </div>
                  </td>
                  <td className="font-mono text-slate-600 text-[12.5px]">{u.email}</td>
                  <td><span className={`inline-block px-2 py-0.5 rounded border text-[11px] font-medium ${ROLE_BADGE[u.role] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{ROLE_LABELS[u.role] || u.role}</span></td>
                  <td className="text-slate-600 text-[12.5px]">{u.department || <span className="text-slate-400">—</span>}</td>
                  <td className="font-mono text-slate-600 text-[12.5px]">{u.phone || <span className="text-slate-400">—</span>}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => openEdit(u)} title="Edit" className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800" data-testid={`edit-user-${u.email}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setResetTarget(u)} title="Reset password" className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#7C1D3A]" data-testid={`reset-user-${u.email}`}><KeyRound className="h-4 w-4" /></button>
                      {u.id !== me?.id && (
                        <button onClick={() => toggleActive(u)} title={u.is_active === false ? "Reactivate" : "Deactivate"} className={`p-1.5 rounded hover:bg-slate-100 ${u.is_active === false ? "text-emerald-600 hover:text-emerald-700" : "text-slate-500 hover:text-amber-600"}`} data-testid={`toggle-user-${u.email}`}>
                          {u.is_active === false ? <UserCheck className="h-4 w-4" /> : <UserX className="h-4 w-4" />}
                        </button>
                      )}
                      {u.id !== me?.id && (
                        <button onClick={() => del(u)} title="Delete" className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600" data-testid={`del-user-${u.email}`}><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-slate-500">No users match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit user dialog */}
      <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-brand" /> Edit user</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12.5px] font-mono text-slate-600">{editUser.email}</div>
              <Input placeholder="Full name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Department" value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} data-testid="edit-user-department" />
              </div>
              <Input placeholder="Phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-user-phone" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-brand hover:bg-brand-dark text-white" data-testid="edit-user-save">{busy ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-brand" /> Reset password</DialogTitle></DialogHeader>
          {resetTarget && (
            <div className="space-y-3">
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900">You're setting a new password for <b>{resetTarget.email}</b>. Share it securely — they should change it on next login.</div>
              <Input type="text" placeholder="New password (min 6 chars)" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="reset-new-password" className="font-mono" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setNewPwd(""); }}>Cancel</Button>
            <Button onClick={doReset} disabled={busy} className="bg-brand hover:bg-brand-dark text-white" data-testid="reset-password-confirm">{busy ? "Saving…" : "Set new password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk upload dialog */}
      <BulkUploadDialog open={bulkOpen} onClose={() => { setBulkOpen(false); load(); }} />
      {/* One-time Asset IQ gatekeeper import */}
      <ImportGatekeepersDialog open={importOpen} onClose={() => { setImportOpen(false); load(); }} />
    </div>
  );
}

function BulkUploadDialog({ open, onClose }) {
  const [csv, setCsv] = useState("");
  const [fileName, setFileName] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [parsedRows, setParsedRows] = useState([]);  // used when a spreadsheet is picked (bypasses CSV pasting)

  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((ln) => {
      const cells = ln.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
      const row = {};
      headers.forEach((h, i) => { row[h] = cells[i] || ""; });
      return row;
    });
  };

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setReport(null);
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    try {
      if (ext === "csv" || file.type === "text/csv") {
        const text = await file.text();
        setCsv(text);
        setParsedRows([]);   // clear — we'll re-parse the pasted CSV at run time
      } else if (["xlsx", "xls", "xlsm", "xlsb", "ods"].includes(ext)) {
        // Dynamically import xlsx only when a spreadsheet is picked so it doesn't bloat the login bundle
        const XLSX = await import("xlsx");
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        // header:1 → array of arrays. First row = headers.
        const rows2d = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: false });
        if (!rows2d.length) { toast.error("Spreadsheet is empty"); return; }
        const headers = rows2d[0].map((h) => String(h || "").trim().toLowerCase());
        const objs = rows2d.slice(1).filter((r) => r.some((c) => String(c).trim() !== "")).map((r) => {
          const o = {};
          headers.forEach((h, i) => { o[h] = String(r[i] ?? "").trim(); });
          return o;
        });
        setParsedRows(objs);
        // Render a friendly CSV echo so the user can see what was picked up
        setCsv([headers.join(","), ...objs.map((o) => headers.map((h) => `"${(o[h] || "").replace(/"/g, '""')}"`).join(","))].join("\n"));
        toast.success(`Loaded ${objs.length} row(s) from ${file.name}`);
      } else {
        toast.error(`Unsupported file type: .${ext}. Use CSV or Excel.`);
      }
    } catch (ex) {
      toast.error(`Failed to parse ${file.name}: ${ex.message || ex}`);
    }
  };

  const downloadTemplate = (fmt) => {
    const headers = ["email", "name", "role", "phone", "department", "password"];
    const sampleRows = [
      ["priya@bbsglobal.in", "Priya Sen", "cad",          "+919812345678", "CAD",         ""],
      ["arun@bbsglobal.in",  "Arun Menon", "production",   "",              "Production",  ""],
      ["meera@bbsglobal.in", "Meera Iyer", "merchandiser", "+919999999999", "Merchandise", ""],
    ];
    if (fmt === "xlsx") {
      import("xlsx").then((XLSX) => {
        const ws = XLSX.utils.aoa_to_sheet([headers, ...sampleRows]);
        ws["!cols"] = headers.map(() => ({ wch: 22 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Users");
        XLSX.writeFile(wb, "opsiq-users-template.xlsx");
      });
    } else {
      const csvText = [headers, ...sampleRows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csvText], { type: "text/csv" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "opsiq-users-template.csv"; a.click();
      URL.revokeObjectURL(a.href);
    }
  };

  const run = async (dry_run) => {
    // Prefer parsed rows if a spreadsheet was picked; otherwise re-parse the CSV textarea
    const rows = parsedRows.length ? parsedRows : parseCsv(csv);
    if (!rows.length) { toast.error("Load a file or paste CSV with at least one data row"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/users/bulk-upload", { rows, dry_run });
      setReport(data);
      if (!dry_run) { toast.success(`Created ${data.created.length}, updated ${data.updated.length}`); }
      else toast.success("Dry run complete — review report below");
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk upload failed"); }
    setBusy(false);
  };

  const reset = () => { setCsv(""); setFileName(""); setParsedRows([]); setReport(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && (reset(), onClose?.())}>
      <DialogContent className="max-w-2xl" data-testid="bulk-upload-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-brand" /> Bulk upload users</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12.5px] text-slate-600">
            Columns: <span className="font-mono">email, name, role, phone, department, password</span>. <b>Role</b> must be one of admin / admin_addl / cad / production / merchandiser / management / line_supervisor / ie_assistant / floor_supervisor / purchase / stores / stores_manager / stores_jr_asst / docs_executive / packing_supervisor / guard. Password is optional — a 10-char temp password is generated if blank. <b>Excel</b> and <b>CSV</b> are both accepted.
          </div>

          {/* Download template */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 mr-1">Need a template?</span>
            <button type="button" onClick={() => downloadTemplate("xlsx")} className="text-[11.5px] font-semibold text-brand hover:underline" data-testid="download-template-xlsx">Download .xlsx</button>
            <span className="text-slate-300">·</span>
            <button type="button" onClick={() => downloadTemplate("csv")} className="text-[11.5px] font-semibold text-brand hover:underline" data-testid="download-template-csv">Download .csv</button>
          </div>

          {/* File picker */}
          <div className="rounded-md border border-slate-200 p-3 bg-white">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-[12.5px] font-semibold text-slate-700" data-testid="bulk-upload-file-label">
                <Upload className="h-4 w-4" /> Choose file (.xlsx / .xls / .csv)
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,.xlsm,.xlsb,.ods,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="bulk-upload-file-input"
                />
              </label>
              {fileName && <div className="text-[12px] font-mono text-slate-600">{fileName}{parsedRows.length ? ` · ${parsedRows.length} rows` : ""}</div>}
              {(csv || fileName) && (
                <button type="button" onClick={reset} className="text-[11.5px] text-slate-500 hover:text-rose-600 ml-auto" data-testid="bulk-upload-clear">Clear</button>
              )}
            </div>
            <div className="mt-2 text-[11.5px] text-slate-400">or paste CSV directly below</div>
          </div>

          <textarea
            value={csv}
            onChange={(e) => { setCsv(e.target.value); setParsedRows([]); setFileName(""); }}
            placeholder={"email,name,role,phone,department,password\npriya@bbsglobal.in,Priya S,cad,+919812345678,CAD,\narun@bbsglobal.in,Arun M,production,,Production,"}
            className="w-full h-40 rounded-md border border-slate-200 p-2 font-mono text-[11.5px]"
            data-testid="bulk-upload-csv"
          />
          {report && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700 max-h-56 overflow-y-auto" data-testid="bulk-upload-report">
              <div className="font-semibold text-slate-900 mb-1">{report.dry_run ? "Dry-run report" : "Commit report"}</div>
              <div className="font-mono text-[11.5px]">
                <div>Created: {report.created.length}</div>
                <div>Updated: {report.updated.length}</div>
                <div>Errors: {report.errors.length}</div>
                {!!report.created.length && (
                  <div className="mt-2">
                    <div className="font-semibold">Created users:</div>
                    {report.created.map((c, i) => (
                      <div key={i} className="pl-3">{c.email}{c.temp_password ? ` · temp: ${c.temp_password}` : ""}</div>
                    ))}
                  </div>
                )}
                {!!report.errors.length && (
                  <div className="mt-2 text-rose-700">
                    <div className="font-semibold">Errors:</div>
                    {report.errors.map((e, i) => (<div key={i} className="pl-3">Row {e.row}: {e.reason}{e.email ? ` (${e.email})` : ""}</div>))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose?.(); }}>Close</Button>
          <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="bulk-dry-run-btn">{busy ? "…" : "Dry Run"}</Button>
          <Button onClick={() => run(false)} disabled={busy} className="bg-brand hover:bg-brand-dark text-white" data-testid="bulk-commit-btn">{busy ? "Importing…" : "Import Now"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// ImportGatekeepersDialog — one-time cutover import for Asset IQ
// Gate Keepers. Accepts a JSON list from the Asset IQ admin export
// (their option "b" — fresh temp passwords), imports each row as
// role='guard', and forces them to change their password on first
// login (server sets must_change_password=true).
// ─────────────────────────────────────────────────────────────
function ImportGatekeepersDialog({ open, onClose }) {
  const [jsonText, setJsonText] = useState("");
  const [fileName, setFileName] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setReport(null);
    try {
      const text = await file.text();
      setJsonText(text);
      toast.success(`Loaded ${file.name}`);
    } catch (ex) {
      toast.error(`Could not read ${file.name}: ${ex.message || ex}`);
    }
  };

  const parseUsers = () => {
    // Accepts either an array of users, or {users: [...]}. Silently tolerates trailing
    // commas / BOM / a top-level "gate_keepers" alias so admins don't get stuck on shape.
    const raw = jsonText.trim().replace(/^\uFEFF/, "");
    if (!raw) return [];
    let parsed;
    try { parsed = JSON.parse(raw); }
    catch (e) { throw new Error(`Invalid JSON: ${e.message}`); }
    const arr = Array.isArray(parsed) ? parsed
      : Array.isArray(parsed.users) ? parsed.users
      : Array.isArray(parsed.gate_keepers) ? parsed.gate_keepers
      : null;
    if (!arr) throw new Error("Expected an array of users or {users:[...]}");
    return arr.map((u) => ({
      email: (u.email || "").trim(),
      name: (u.name || u.full_name || "").trim(),
      phone: (u.phone || u.mobile || "").trim(),
      department: (u.department || u.dept || "Security").trim(),
      temp_password: (u.temp_password || u.password || u.pwd || "").trim(),
      is_active: u.is_active === undefined ? true : !!u.is_active,
    }));
  };

  const run = async (dry_run) => {
    let users;
    try { users = parseUsers(); }
    catch (e) { toast.error(e.message); return; }
    if (!users.length) { toast.error("No users found in JSON"); return; }
    // Client-side pre-validation so we don't rely solely on backend errors.
    const missing = users.filter((u) => !u.email || !u.name || !u.temp_password);
    if (missing.length) {
      toast.error(`${missing.length} row(s) missing email / name / temp_password — please fix and retry`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/users/import-gatekeepers", { users, dry_run });
      setReport(data);
      if (!dry_run) {
        toast.success(`Imported ${data.imported_count} guard(s) · Skipped ${data.skipped_count}`);
      } else {
        toast.success(`Dry-run: ${data.imported_count} would be imported, ${data.skipped_count} skipped`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setJsonText(""); setFileName(""); setReport(null); };

  const downloadTemplate = () => {
    const sample = {
      users: [
        { email: "guard.one@bbsglobal.in", name: "Guard One", phone: "+919000000001", department: "Security", temp_password: "TempPass!123", is_active: true },
        { email: "guard.two@bbsglobal.in", name: "Guard Two", phone: "+919000000002", department: "Security", temp_password: "TempPass!456", is_active: true },
      ],
    };
    const blob = new Blob([JSON.stringify(sample, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "asset-iq-gatekeepers-template.json";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && (reset(), onClose?.())}>
      <DialogContent className="max-w-2xl" data-testid="import-gatekeepers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-brand" /> Import Gate Keepers from Asset IQ
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900">
            <div className="font-semibold mb-1">One-time cutover import</div>
            Paste the JSON export you got from Asset IQ (their option "b" — fresh temp passwords). Each row is created here as role <span className="font-mono font-semibold">guard</span>. Existing Ops IQ emails are <b>skipped</b> — never overwritten. Imported guards are forced to change their password on first login.
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 mr-1">Need a template?</span>
            <button type="button" onClick={downloadTemplate} className="text-[11.5px] font-semibold text-brand hover:underline inline-flex items-center gap-1" data-testid="download-import-template">
              <Download className="h-3.5 w-3.5" /> Download sample .json
            </button>
          </div>

          <div className="rounded-md border border-slate-200 p-3 bg-white">
            <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-[12.5px] font-semibold text-slate-700" data-testid="import-file-label">
              <Upload className="h-4 w-4" /> Choose file (.json)
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="import-file-input"
              />
            </label>
            {fileName && <span className="text-[12px] font-mono text-slate-600 ml-3">{fileName}</span>}
          </div>

          <textarea
            value={jsonText}
            onChange={(e) => { setJsonText(e.target.value); setFileName(""); }}
            placeholder={'{\n  "users": [\n    {"email": "guard1@asset.iq", "name": "Guard One", "phone": "+91…", "department": "Security", "temp_password": "TempPass!123"}\n  ]\n}'}
            className="w-full h-40 rounded-md border border-slate-200 p-2 font-mono text-[11.5px]"
            data-testid="import-json"
          />

          {report && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700 max-h-56 overflow-y-auto" data-testid="import-report">
              <div className="font-semibold text-slate-900 mb-1">{report.dry_run ? "Dry-run report" : "Import report"}</div>
              <div className="font-mono text-[11.5px]">
                <div>Total: {report.total} · Imported: {report.imported_count} · Skipped: {report.skipped_count} · Errors: {report.error_count}</div>
                {!!report.imported?.length && (
                  <div className="mt-2">
                    <div className="font-semibold text-emerald-700">Imported:</div>
                    {report.imported.map((r, i) => (<div key={i} className="pl-3">{r.email} · {r.name}{r.dry_run ? " · dry-run" : ""}</div>))}
                  </div>
                )}
                {!!report.skipped?.length && (
                  <div className="mt-2 text-amber-800">
                    <div className="font-semibold">Skipped:</div>
                    {report.skipped.map((r, i) => (<div key={i} className="pl-3">{r.email} — {r.reason}</div>))}
                  </div>
                )}
                {!!report.errors?.length && (
                  <div className="mt-2 text-rose-700">
                    <div className="font-semibold">Errors:</div>
                    {report.errors.map((r, i) => (<div key={i} className="pl-3">{r.email}: {r.reason}</div>))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose?.(); }}>Close</Button>
          <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="import-dry-run-btn">{busy ? "…" : "Dry Run"}</Button>
          <Button onClick={() => run(false)} disabled={busy} className="bg-brand hover:bg-brand-dark text-white" data-testid="import-commit-btn">
            {busy ? "Importing…" : "Import Guards"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

